import { TrashIcon } from "@components/Icons";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import TableWithPagination from "./TableWithPagination";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";

const NoticeInformationTable = ({
  data = [],
  className = "",
  onDataChange = null,
  onRowClick = null,
  onDelete = null,
  maxRowsPerPage = 10,
  showPagination = true,
  scrollableBody = false,
  seq = "verificationReportDetailSeq",
  loading = false,
  children,
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [tableData, setTableData] = useState(data);
  const [currentPage, setCurrentPage] = useState(1);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY. NOTICE_INFORMATION_VIEW_LIST, DEFAULT_ITEMS_PER_PAGE);

  const displayData = useMemo(() => {
    return tableData.length > 0 ? tableData : data;
  }, [data, tableData]);

  // Update table data when data prop changes
  useEffect(() => {
    if (data?.length >= 0) {
      setTableData(data);
    }
  }, [data]);

  // Notify parent component when table data changes
  useEffect(() => {
    if (onDataChange && tableData.length > 0) {
      onDataChange(tableData);
    }
  }, [tableData, onDataChange]);

  // Calculate pagination
  const totalData = displayData.length;
  const totalPages = Math.ceil(totalData / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = displayData.slice(startIndex, endIndex);

  useEffect(() => {
    if (totalPages === 0) {
      setCurrentPage(1);
      return;
    }

    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const renderCell = (row, columnKey, index) => {
    switch (columnKey) {
      case "no":
        // Calculate the actual row number considering pagination
        return (
          <div className="flex items-center justify-center">
            <span className={`text-sm ${colors.textPrimary}`}>{startIndex + index + 1}</span>
          </div>
        );

      case "componentName":
        const componentVersion = row.componentVersion || "";
        const licenseName = row.licenseName || "";
        const isVersionLong = componentVersion.length > 15;

        return (
          <div>
            <div className={`text-sm font-medium ${colors.textPrimary} flex items-center gap-2`}>
              <span className={`${colors.textPrimary}`}>{row.componentName || row.component}</span>
              {isVersionLong ? (
                <span
                  className={`text-xs ${colors.textMuted} truncate max-w-[120px] inline-block align-bottom`}
                  title={componentVersion}
                >
                  ({componentVersion})
                </span>
              ) : (
                <span className={`text-xs ${colors.textMuted} inline-block align-bottom`} title={componentVersion}>
                  ({componentVersion})
                </span>
              )}
            </div>
            <div className={`text-xs ${colors.textMuted}`}>{licenseName}</div>
          </div>
        );

      case "url":
        return (
          <div className="max-w-full truncate">
            <span className={`text-sm ${colors.textPrimary} truncate block`} title={row.url || "-"}>
              {row.url || "-"}
            </span>
          </div>
        );

      case "copyright":
        return (
          <div className="max-w-full truncate">
            <span className={`text-sm ${colors.textPrimary} truncate block`} title={row.copyright || "-"}>
              {row.copyright || "-"}
            </span>
          </div>
        );

      case "licenseText":
        const licenseText = row.licenseText || "";

        return (
          <div className="max-w-full truncate">
            <span className={`text-sm ${colors.textPrimary} truncate block`} title={licenseText}>
              {licenseText || "-"}
            </span>
          </div>
        );

      case "action":
        return (
          <div className="flex items-center justify-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onDelete) {
                  const actualIndex = displayData.findIndex(
                    (item) =>
                      item[seq] === row[seq] ||
                      (item.componentName === row.componentName && item.componentVersion === row.componentVersion),
                  );
                  onDelete(row, actualIndex);
                }
              }}
              className={`${colors.iconDelete} ${colors.hoverTextDanger} transition-colors p-1`}
              title={t("common.delete-button")}
            >
              <TrashIcon></TrashIcon>
            </button>
          </div>
        );

      default:
        return <span className={`text-sm ${colors.textPrimary}`}>{row[columnKey] || ""}</span>;
    }
  };

  // Column configuration for Notice Information
  const columns = ["no", "componentName", "url", "copyright", "licenseText", "action"];

  const columnConfig = {
    no: {
      title: t("common.no"),
      customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
      style: { width: "8%", textAlign: "center" },
    },
    componentName: {
      title: t("project.notice-infor.component-name"),
      customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
      style: { width: "15%" },
    },
    url: {
      title: t("common.url"),
      customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
      style: { width: "20%", whiteSpace: "nowrap", textOverflow: "ellipsis" },
      truncate: true,
    },
    copyright: {
      title: t("project.notice-infor.copyright"),
      customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
      style: { width: "20%", whiteSpace: "nowrap", textOverflow: "ellipsis" },
      truncate: true,
    },
    licenseText: {
      title: t("open-source.license-text"),
      customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
      style: { width: "20%", whiteSpace: "nowrap", textOverflow: "ellipsis" },
      truncate: true,
    },
    action: {
      title: t("common.action"),
      customRender: (value, row, columnKey, index) => renderCell(row, columnKey, index),
      style: { width: "7%", textAlign: "center" },
    },
  };

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
        handleItemsPerPageChange(newItemsPerPage);
        setCurrentPage(1); // Reset to page 1 when changing number of rows
    };

  const handleRowClick = (row, index) => {
    if (onRowClick) {
      // Calculate the actual index using pagination offset
      const actualIndex = startIndex + index;
      onRowClick(row, actualIndex);
    }
  };

  return (
    <div className={`relative ${scrollableBody ? "flex flex-1 flex-col min-h-0" : ""} ${className}`} data-testid="notice-information-table">
      {/* Header Section */}
      {children}

      <TableWithPagination
        dataTestId="notice-information-table-grid"
        columns={columns}
        data={paginatedData}
        colSize={12}
        maxPagesToShow={5}
        maxRowsPerPage={itemsPerPage}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        loading={loading}
        totalPages={totalPages}
        totalData={totalData}
        columnConfig={columnConfig}
        showPagination={showPagination}
        onItemsPerPageChange={handleItemsPerPageChangeWithReset}
        onRowClick={handleRowClick}
        rowKey={seq}
        tableStyle="default"
        scrollableBody={scrollableBody}
        rowClassName={(row) =>
          `cursor-pointer ${colors.hoverTableRow} transition-colors ${
            row.isEdited ? colors.bgEditedRow : ""
          }`
        }
        showItemsPerPageSelector={true}
      />
    </div>
  );
};

export default NoticeInformationTable;
